import React, { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import Card from "components/card";
import { fetchFleetRows } from "utils/fleetRoster";
import { readToken } from "utils/session";
import { BATTERY_OPTIONS, batteryBand, motionLabel } from "./FleetViewTable";

Chart.register(ArcElement, Tooltip, Legend);

export const CHART_COLORS = ["#42AACC", "#EE5D50", "#4299e1", "#A3AED0", "#D69E2E", "#809D3C", "#A35C7A"];

const MOTION_SLICES = [
  { id: "HALTED", label: "Halted" },
  { id: "WALKING", label: "Walking" },
  { id: "MOVING", label: "Moving" },
  { id: "NOT REPORTING", label: "Not reporting" },
];

function countBy(rows, sliceOf) {
  const counts = new Map();
  rows.forEach((row) => {
    const id = sliceOf(row);
    if (!id) return;
    counts.set(id, (counts.get(id) || 0) + 1);
  });
  return counts;
}

function FleetSummaryPieCharts() {
  const [rows, setRows] = useState(null);
  const userKey = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!userKey) {
      setRows([]);
      return;
    }
    fetchFleetRows({ apiBaseUrl, userKey })
      .then((rows) => {
        setRows(rows);
      })
      .catch((error) => {
        console.error("Error fetching fleet health:", error);
        setRows([]);
      });
  }, [userKey, apiBaseUrl]);

  if (rows == null) return <div>Loading...</div>;

  const motionCounts = countBy(rows, (row) => motionLabel(row) || "UNKNOWN");
  const motionSlices = MOTION_SLICES.map((slice) => ({ ...slice, value: motionCounts.get(slice.id) || 0 }));
  motionCounts.forEach((value, id) => {
    if (!MOTION_SLICES.some((slice) => slice.id === id)) {
      motionSlices.push({ id, label: id === "UNKNOWN" ? "Unknown" : id, value });
    }
  });

  const batteryCounts = countBy(rows, (row) => batteryBand(row.batstat) || "unknown");
  const batterySlices = BATTERY_OPTIONS.map((option) => ({
    id: option.id,
    label: option.label,
    value: batteryCounts.get(option.id) || 0,
  }));
  if (batteryCounts.get("unknown")) {
    batterySlices.push({ id: "unknown", label: "No reading", value: batteryCounts.get("unknown") });
  }

  const charts = [
    { name: "Fleet Health", slices: motionSlices },
    { name: "Fleet Battery Health", slices: batterySlices },
  ];

  return (
    <div className="grid gap-5 md:grid-cols-2">
      {charts.map((chart) => (
        <Card key={chart.name} extra="rounded-[20px] p-3 h-[450px] relative">
          <div className="flex flex-row justify-between px-3 pt-2">
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">{chart.name}</h4>
          </div>
          <div className="mb-auto flex h-[380px] w-full items-center justify-center" style={{ maxHeight: "300px", marginTop: "20px" }}>
            <Pie
              data={{
                labels: chart.slices.map((slice) => slice.label),
                datasets: [
                  {
                    data: chart.slices.map((slice) => slice.value),
                    backgroundColor: chart.slices.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: true, position: "bottom" },
                },
              }}
            />
          </div>
        </Card>
      ))}
    </div>
  );
}

export default FleetSummaryPieCharts;
